import { SubflowMessengerAction } from 'lib-shared-types-api';
import { useCallback, useContext, useState } from 'react';

import { useCustomForms } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/useCustomForms';
import { FlowContext } from 'app/views/layout/views/settings/views/messenger/views/editFlow';
import { FlowExtraDataFetch } from 'components/powerFlowShared/components/extraDataFetch';
import { FlowEditorChrome, FlowType, NodeDataT } from 'fable/flowEditor';
import { FlowT } from 'hocs/flows/api/types';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { ElevioIntegrationProvider } from './elevioIntegrationProvider';
import styles from './flow.module.scss';
import { FlowMessengerAction } from './flow.types';
import { formatFlowForBackend } from './formatFlowForBackend';
import { getMainFlowNodesBasedOnFeatureFlags } from './getNodesBasedOnFeatureFlags';
import { useQueryElevioIntegration } from './hooks';

export const MessengerFlow = (): JSX.Element => {
  const notify = useNotifications();
  const [invalidNodes, setInvalidNodes] = useState<null | string[]>(null);
  const { forms } = useCustomForms();
  const {
    messengerId,
    actions,
    active,
    setActions,
    setActive,
    initialFlow: flow,
    flowId,
    update,
    subflows,
    mainFlowSaveStatusData,
    navigateAwayPromptEnabled,
  } = useContext(FlowContext);

  const elevioIntegration = useQueryElevioIntegration(messengerId);

  const nodeTypes = getMainFlowNodesBasedOnFeatureFlags(
    useFeatureFlag([
      'integrations-for-dixa-messenger',
      'conversation-assistant-for-dixa-messenger',
      'chatbots-integration-settings',
      'has-custom-forms-feature',
      'subflows-in-dixa-messenger-flow-builder',
      'has-custom-forms-v2-feature',
    ]),
  );

  const handleOnChange = useCallback(
    ({ actions: newActions, active: newActive }: NodeDataT) => {
      if (
        !mainFlowSaveStatusData.hasUnsavedChanges ||
        actions !== newActions ||
        active !== newActive
      ) {
        mainFlowSaveStatusData.setStatusToUnsaved();
        setInvalidNodes(null);
        setActions(newActions as FlowMessengerAction[]);
        setActive(newActive);
      }
    },
    [actions, active, setActive, setActions, mainFlowSaveStatusData],
  );

  const getCurrentFlow = useCallback(
    (
      isActive: boolean,
    ): FlowT<FlowMessengerAction, SubflowMessengerAction> | false => {
      if (flow && actions !== null) {
        return {
          ...flow,
          actions: [...actions] as FlowMessengerAction[],
          active: active || isActive,
        };
      }
      return false;
    },
    [actions, active, flow],
  );

  const handleOnActivate = useCallback(async () => {
    const updatedFlow = getCurrentFlow(true);
    if (updatedFlow && flowId) {
      await update(flowId, updatedFlow);
      mainFlowSaveStatusData.setStatusToSaved();
      setActive(true);
    }
  }, [flowId, getCurrentFlow, setActive, update, mainFlowSaveStatusData]);

  function validateFlow(flow: FlowT) {
    // Make sure not unconnected ports are present
    const invalidNodes = flow.actions
      .filter((action) => action.nodeType === 'action')
      .map((action) => action.id);

    if (invalidNodes.length > 0) {
      return { invalidNodes, isValid: false };
    }

    return { isValid: true };
  }

  const handleOnSave = useCallback(async () => {
    const updatedFlow = getCurrentFlow(false);
    if (updatedFlow && flowId) {
      try {
        const { isValid, invalidNodes } = validateFlow(updatedFlow);

        if (!isValid) {
          setInvalidNodes(invalidNodes!);
          notify(
            {
              title: 'Could not save',
              text: `There are unconnected nodes.`,
              time: 5000,
            },
            NotificationType.Error,
          );
          return;
        }

        const formattedFlow = formatFlowForBackend(updatedFlow);

        // Optimistically set the status to saved
        mainFlowSaveStatusData.setStatusToSaved();

        await update(flowId, formattedFlow);

        notify(
          { title: 'Saved', text: `"${flow?.name}" is saved.`, time: 3000 },
          NotificationType.Success,
        );
      } catch (err: unknown) {
        // Revert the optimistic status to unsaved
        mainFlowSaveStatusData.setStatusToUnsaved();

        notify(
          {
            title: 'Could not save',
            text: `There was an issue when "${flow?.name}" was saved. ${err}`,
          },
          NotificationType.Error,
        );
      }
    }
  }, [
    getCurrentFlow,
    flowId,
    update,
    notify,
    flow?.name,
    mainFlowSaveStatusData,
  ]);

  const shouldPreventNavigation =
    navigateAwayPromptEnabled && mainFlowSaveStatusData.hasUnsavedChanges;

  return (
    <ElevioIntegrationProvider elevioIntegration={elevioIntegration}>
      <div className={styles.root}>
        {flow && (
          <FlowExtraDataFetch
            actions={actions}
            render={(extraData) => (
              <FlowEditorChrome
                disableTitleEdit
                errorMessage={null}
                navigatePromptProps={{ when: shouldPreventNavigation }}
                extraData={{
                  name: flow.name,
                  subflows,
                  nodeTypes,
                  forms,
                  ...extraData,
                }}
                flowType={FlowType.FLOW_TYPE_MESSENGER}
                goBackPath="/settings/dixa-messenger"
                backButtonText="Back to messengers"
                hasUnsavedChanges={mainFlowSaveStatusData.hasUnsavedChanges}
                initialData={flow}
                invalidNodes={invalidNodes}
                nodeTypes={nodeTypes}
                onActivate={handleOnActivate}
                onChange={handleOnChange}
                onSave={handleOnSave}
              />
            )}
          />
        )}
      </div>
    </ElevioIntegrationProvider>
  );
};
